import { getApiError } from '@centric-os/helpers';
import { useAuthStore } from '@centric-os/stores/auth/use-auth-store';
import { useLoadingStore } from '@centric-os/stores/loading/use-loading-store';
import type { ApiResponseError, AxiosCustomConfig } from '@centric-os/types';
import axios, { type AxiosError, type AxiosInstance } from 'axios';
import { isEmpty, isNil } from 'lodash-es';

const baseURL: string = import.meta.env.VITE_CDL_ENDPOINT_BASE_URL;

export const createAxiosInstance = (config: AxiosCustomConfig = {}): AxiosInstance => {
  const api = axios.create({
    baseURL,
    ...config,
    paramsSerializer: (params: Record<string, any>): string => {
      const searchParams = new URLSearchParams();

      Object.keys(params).forEach((key) => {
        const value = params[key];

        if (
          isNil(value) || // Remove null or undefined
          ((Array.isArray(value) || typeof value === 'object') && isEmpty(value)) // Remove empty arrays and objects
        ) {
          return;
        }

        if (Array.isArray(value) && value.some((item) => item !== null && typeof item === 'object')) {
          // Serialize arrays of objects as JSON (e.g. OR filter arrays)
          searchParams.append(key, JSON.stringify(value));
        } else if (Array.isArray(value)) {
          // Serialize arrays of primitives as repeated keys to follow standard CDL params format
          value.forEach((item) => searchParams.append(key, item.toString()));
        } else if (typeof value === 'object') {
          // Serialize objects as JSON
          searchParams.append(key, JSON.stringify(value));
        } else {
          // Serialize primitives as strings
          searchParams.append(key, value.toString());
        }
      });

      return searchParams.toString();
    },
  });

  // @ts-expect-error FIXME: argument type mismatch (TS2345)
  api.interceptors.request.use(async (req: AxiosCustomConfig) => {
    if (req.url?.startsWith('/user/auth')) return req;

    const authStore = useAuthStore();
    const loadingStore = useLoadingStore();
    const token = await authStore.getValidAccessToken();

    if (token) {
      if (!config?.skipAuthorization && !req.headers.authorization) {
        req.headers.authorization = `Bearer ${token}`;

        loadingStore.loading = true;
      } else if (config?.skipAuthorization) {
        loadingStore.loading = true;
      }
    }

    return req;
  });

  api.interceptors.response.use(
    (response) => {
      useLoadingStore().loading = false;

      return response;
    },
    (error: AxiosError<ApiResponseError>) => {
      useLoadingStore().loading = false;
      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }
      return Promise.reject(getApiError(error));
    },
  );

  return api;
};
